import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiInfo, FiClock, FiTrendingUp, FiStar, FiTarget, FiMap } = FiIcons;

const AdventureSelector = ({ onSelectAdventure, currentAdventure }) => {
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [error, setError] = useState(null);

  const adventureFiles = [
    {
      file: 'edmonton-cityofchamps.json',
      icon: FiStar,
      color: 'from-green-400 to-emerald-600'
    },
    {
      file: 'edmonton-Oddyssey.json', 
      icon: FiTrendingUp,
      color: 'from-blue-400 to-indigo-600'
    },
    {
      file: 'edmonton-whyteavewench.json',
      icon: FiTarget,
      color: 'from-purple-400 to-pink-600'
    },
    {
      file: 'edmonton-afterhours.json',
      icon: FiMap,
      color: 'from-yellow-400 to-red-600'
    }
    //     {
    //   file: 'beginner-cave.json',
    //   icon: FiStar,
    //   color: 'from-green-400 to-emerald-600'
    // },
    // {
    //   file: 'intermediate-depths.json', 
    //   icon: FiTrendingUp,
    //   color: 'from-blue-400 to-indigo-600'
    // },
    // {
    //   file: 'expert-abyss.json',
    //   icon: FiTarget,
    //   color: 'from-purple-400 to-pink-600'
    // },
    // {
    //   file: 'alberta-cave-quest.json',
    //   icon: FiMap,
    //   color: 'from-yellow-400 to-red-600'
    // }
  ];

  useEffect(() => {
    loadAdventures();
  }, []);

  const loadAdventures = async () => {
    try {
      setError(null);
      const loadedAdventures = await Promise.all(
        adventureFiles.map(async (adventure) => {
          try {
            // Try the public/adventures path (this is the correct one)
              const response = await fetch(`/adventures/${adventure.file}`);
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();

            return {
              ...data,
              id: adventure.file.replace('.json', ''),
              icon: adventure.icon,
              color: adventure.color
            };
          } catch (error) {
            console.error(`Failed to load ${adventure.file}:`, error);
            return null;
          }
        })
      );

      const validAdventures = loadedAdventures.filter(Boolean);
      
      if (validAdventures.length === 0) {
  throw new Error('No adventures could be loaded. Make sure the adventure files exist in public/adventures/ and are accessible at /adventures/');
      }
      
      setAdventures(validAdventures);
    } catch (error) {
      console.error('Error loading adventures:', error);
  setError('Failed to load adventures. Please check that adventure files are available in public/adventures/ and accessible at /adventures/.');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'text-green-400';
      case 'intermediate': return 'text-blue-400'; 
      case 'expert': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-300">Loading adventures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-4">Adventure Loading Error</h2>
          <p className="text-slate-300 mb-6">{error}</p>
          <button 
            onClick={loadAdventures}
            className="bg-blue-600 hover:bg-blue-500 text-white py-2 px-6 rounded-lg font-semibold"
          >
            Try Again
          </button>
          <div className="mt-4 p-3 bg-slate-800 rounded text-left text-sm text-slate-400">
            <p><strong>Debug Info:</strong></p>
            <p>Looking for files at: /adventures/</p>
            <p>Expected files: beginner-cave.json, intermediate-depths.json, expert-abyss.json</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600 mb-4">
            Cave Adventures
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Choose your adventure and explore mysterious caves filled with puzzles, treasures, and ancient secrets.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {adventures.map((adventure) => (
            <div
              key={adventure.id}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600 transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${adventure.color} flex items-center justify-center mb-4 mx-auto`}>
                <SafeIcon icon={adventure.icon} className="text-2xl text-white" />
              </div>

              <h3 className="text-2xl font-bold text-white text-center mb-3">
                {adventure.title}
              </h3>

              <p className="text-slate-300 text-center mb-4 min-h-[3rem]">
                {adventure.description}
              </p>

              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Difficulty:</span>
                  <span className={`font-semibold ${getDifficultyColor(adventure.difficulty)}`}>
                    {adventure.difficulty}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Duration:</span>
                  <div className="flex items-center gap-1">
                    <SafeIcon icon={FiClock} className="text-slate-400" />
                    <span className="text-slate-300 text-sm">
                      {adventure.estimatedTime}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => onSelectAdventure(adventure)}
                  className={`flex-1 bg-gradient-to-r ${adventure.color} text-white py-3 px-4 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  <SafeIcon icon={FiPlay} />
                  Play
                </button>
                <button
                  onClick={() => setSelectedInfo(selectedInfo === adventure.id ? null : adventure.id)}
                  className="bg-slate-700 text-slate-300 py-3 px-4 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  <SafeIcon icon={FiInfo} />
                </button>
              </div>

              {selectedInfo === adventure.id && (
                <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <h4 className="font-semibold text-white mb-2">Adventure Details</h4>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p><strong>Rooms:</strong> {Object.keys(adventure.rooms || {}).length}</p>
                    <p><strong>Items:</strong> {Object.keys(adventure.items || {}).length}</p>
                    <p><strong>Special Puzzles:</strong> {Object.keys(adventure.specialPuzzles || {}).length}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {currentAdventure && (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full border border-slate-700">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-slate-300">Currently playing: {currentAdventure.title}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdventureSelector;